// A RELATIVE import, not the `@/` alias, and that is load-bearing: `pnpm gate`
// imports this file with a bare `node --experimental-strip-types`, which knows
// nothing about Vite's aliases. The predicate below is the one thing the Worker
// and the 404 page must agree on, so it has to be testable without a bundler.
import { ALL_LOCALES } from '../i18n/locales.ts'

/**
 * Does the Worker's `/en{path}` rescue probe apply to this path?
 *
 * digitopia.co's apex 301s here with the path intact, so old URLs arrive
 * unprefixed while every page on this site lives under a locale. The Worker
 * answers that by asking the assets binding whether `/en{path}` is a real
 * page and, if it is, 301ing there (see `src/worker/index.ts`). The probe is
 * deliberately narrow: not already localised, and not an asset.
 *
 * **Shared rather than written twice, and that is the whole point of the
 * file.** The Worker uses it to decide whether to probe; `NotFound.tsx` uses
 * it to label the `page_not_found` event with whether the rescue even had a
 * shot. Those two answers have to be the same answer — a measurement that
 * says "the probe tried and failed" about a path the probe never looked at
 * sends somebody hunting for a redirect row that would change nothing.
 *
 * Pass a path with any trailing slash already dropped, exactly as the Worker
 * normalises it before the lookup — `/dmi/` and `/dmi` are one URL here.
 */
const LOCALISED = new RegExp(`^/(${ALL_LOCALES.map((l) => l.code).join('|')})(/|$)`)

/** A file extension, which means an asset rather than a page. */
const ASSET = /\.[a-z0-9]+$/i

export function probeApplies(path: string): boolean {
  return path !== '/' && !LOCALISED.test(path) && !ASSET.test(path)
}

/** The Worker's own normalisation: one trailing slash rule for every lookup. */
export function normalisePath(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}
